import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/* eslint-disable @atlaskit/design-system/no-nested-styles */
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useEffect, useRef } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { fg } from '@atlaskit/platform-feature-flags';
import { spacing as spacingOptions } from './constants';
import { ANIMATION_EASE_OUT, LINEAR_TRANSITION_SPEED, TRANSITION_SPEED, varSpacing } from './internal/constants';
import Link from './internal/link';
import LinkNew from './internal/link-new';
import Stage from './internal/stage';
var containerStyles = css({
  display: 'grid',
  width: '100%',
  margin: '0 auto',
  padding: "var(--ds-space-0, 0px)",
  // eslint-disable-next-line @atlaskit/design-system/ensure-design-token-usage, @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
  gap: "var(".concat(varSpacing, ")"),
  listStyleType: 'none',
  // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
  '&&': {
    marginBlockStart: "var(--ds-space-500, 40px)"
  }
});
/**
 * __Progress tracker__
 *
 * A progress tracker displays the steps and progress through a journey.
 *
 * - [Examples](https://atlassian.design/components/progress-tracker/examples)
 * - [Code](https://atlassian.design/components/progress-tracker/code)
 * - [Usage](https://atlassian.design/components/progress-tracker/usage)
 */
var ProgressTracker = function ProgressTracker(_ref) {
  var _listInlineStyles;
  var _ref$items = _ref.items,
    items = _ref$items === void 0 ? [] : _ref$items,
    _ref$spacing = _ref.spacing,
    spacing = _ref$spacing === void 0 ? 'cosy' : _ref$spacing,
    _ref$render = _ref.render,
    render = _ref$render === void 0 ? {
      link: function link(_ref2) {
        var item = _ref2.item;
        return (
          // Anchor content is coming from another location
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          fg('platform_progress_tracker_link_migration') ? jsx(LinkNew, item) : jsx(Link, item)
        );
      }
    } : _ref$render,
    _ref$animated = _ref.animated,
    animated = _ref$animated === void 0 ? true : _ref$animated,
    testId = _ref.testId,
    _ref$label = _ref.label,
    label = _ref$label === void 0 ? 'Progress' : _ref$label;
  var prevItems = useRef(items.map(function (stage) {
    return _objectSpread(_objectSpread({}, stage), {}, {
      percentageComplete: 0
    });
  }));
  var previousStages = items.map(function (stage) {
    var _prevItems$current;
    var oldStage = (_prevItems$current = prevItems.current) === null || _prevItems$current === void 0 ? void 0 : _prevItems$current.find(function (st) {
      return st.id === stage.id;
    });
    return !!oldStage ? oldStage : stage;
  });
  useEffect(function () {
    prevItems.current = items;
  }, [items]);
  var progressChanges = items.filter(function (stage, index) {
    return stage.percentageComplete !== previousStages[index].percentageComplete;
  }).length;
  var totalStepsForward = items.filter(function (stage, index) {
    return stage.percentageComplete > previousStages[index].percentageComplete;
  }).length;
  var totalStepsBack = items.filter(function (stage, index) {
    return stage.percentageComplete < previousStages[index].percentageComplete;
  }).length;
  var stepsForward = totalStepsForward;
  var stepsBack = totalStepsBack;
  var progressItems = items.map(function (stage, index) {
    var transitionSpeed = 0;
    var transitionDelay = 0;
    var transitionEasing = progressChanges > 1 ? 'linear' : ANIMATION_EASE_OUT;
    if (animated) {
      transitionSpeed = progressChanges > 1 ? LINEAR_TRANSITION_SPEED : TRANSITION_SPEED;
      if (stage.percentageComplete < previousStages[index].percentageComplete) {
        /**
         * Load each transition sequentially in reverse.
         */
        transitionDelay = (stepsBack - 1) * transitionSpeed;
        stepsBack -= 1;
      } else if (stage.percentageComplete > previousStages[index].percentageComplete) {
        /**
         * Load each transition sequentially.
         */
        transitionDelay = (totalStepsForward - stepsForward) * transitionSpeed;
        stepsForward -= 1;
      }
    }
    return jsx(Stage, {
      transitionSpeed: transitionSpeed,
      transitionDelay: transitionDelay,
      transitionEasing: transitionEasing,
      key: stage.id,
      item: stage,
      render: render
    });
  });
  var listInlineStyles = (_listInlineStyles = {
    gridTemplateColumns: "repeat(".concat(items.length, ", 1fr)")
  }, _defineProperty(_listInlineStyles, varSpacing, spacingOptions[spacing]), _defineProperty(_listInlineStyles, "maxWidth", 8 * 10 * items.length * 2), _listInlineStyles);
  return jsx("ul", {
    "data-testid": testId
    // eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
    ,
    style: listInlineStyles,
    css: containerStyles,
    "aria-label": label
  }, progressItems);
};
export default ProgressTracker;